import { Controller } from "@hotwired/stimulus"

export default class ThingSettingsUpdateController extends Controller {
  static values = {
    url: String,
    prefix: { type: String, default: "Current: " },
    pollingInterval: { type: Number, default: 5000 },
    thingId: String
  }

  static targets = ["label", "input"]

  connect() {
    this.fieldMap = this.indexFields()
    this.startPolling()
    this.addTurboEventListener()
    this.addVisibilityListener()
  }

  disconnect() {
    this.abortController?.abort()
    this.stopPolling()
    this.removeTurboEventListener()
    this.removeVisibilityListener()
  }

  startPolling() {
    this.stopPolling()
    this._polling = true

    const tick = async () => {
      if (!this._polling) return
      await this.poll()
      if (!this._polling) return
      this.timer = setTimeout(tick, this.pollingIntervalValue)
    }

    tick()
  }

  stopPolling() {
    this._polling = false
    clearTimeout(this.timer)
    this.timer = null
  }

  addTurboEventListener() {
    this.turboBeforeVisitHandler = this.handleTurboBeforeVisit.bind(this)
    document.addEventListener("turbo:before-visit", this.turboBeforeVisitHandler)
  }

  removeTurboEventListener() {
    if (this.turboBeforeVisitHandler) {
      document.removeEventListener("turbo:before-visit", this.turboBeforeVisitHandler)
      this.turboBeforeVisitHandler = null
    }
  }

  addVisibilityListener() {
    this.visibilityHandler = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.visibilityHandler)
  }

  removeVisibilityListener() {
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler)
      this.visibilityHandler = null
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopPolling()
    } else {
      this.startPolling()
    }
  }

  handleTurboBeforeVisit() {
    this.abortController?.abort()
    this.stopPolling()
  }

  async poll() {
    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const data = await this.fetchValues(this.abortController.signal)
      if (!data) return

      this.updateLabels(data)
      window.dispatchEvent(new CustomEvent("rtv:updated", {
        detail: { thingId: this.thingIdValue, data }
      }))
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("[ThingSettingsUpdate] Polling error:", error)
      }
    }
  }

  async fetchValues(signal) {
    const response = await fetch(this.urlValue, {
      headers: { "Accept": "application/json" },
      signal
    })
    if (!response.ok) return null

    try {
      const json = await response.json()
      return json.data
    } catch {
      return null
    }
  }

  updateLabels(data) {
    this.labelTargets.forEach(label => {
      const key = label.dataset.rtvKey
      const entry = data[key]
      if (!entry) return

      const rawValue = entry.value
      const unit = label.dataset.rtvUnit

      if (unit) {
        const displayValue = this.formatValue(label, rawValue)
        label.textContent = `${this.prefixValue}${displayValue} ${unit}`
        label.dataset.rtvValue = displayValue
      } else {
        const valueLabels = this.fieldMap[key]?.valueLabels
        const displayLabel = valueLabels?.[String(rawValue)] ?? rawValue
        label.textContent = `${this.prefixValue}${displayLabel}`
        label.dataset.rtvValue = rawValue
      }
    })
  }

  indexFields() {
    const map = {}
    this.inputTargets.forEach(el => {
      const key = el.dataset.rtvKey
      if (!map[key]) map[key] = {}
      if (el.tagName === "SELECT") map[key].select = el
      if (el.dataset.rtvFactor) map[key].factor = parseFloat(el.dataset.rtvFactor)
    })
    this.labelTargets.forEach(el => {
      const key = el.dataset.rtvKey
      if (!map[key]) map[key] = {}
      if (el.dataset.rtvFactor) map[key].factor = parseFloat(el.dataset.rtvFactor)
      if (el.dataset.rtvValueLabels) map[key].valueLabels = JSON.parse(el.dataset.rtvValueLabels)
    })
    return map
  }

  formatValue(element, rawValue) {
    const factor = parseFloat(element.dataset.rtvFactor) || 1
    return (rawValue / factor).toFixed(1)
  }
}
